import {
  Checkbox,
  Dialog,
  DialogContent,
  Fade,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SendIcon from "@material-ui/icons/Send";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { updateUser } from "../Dashboard/actions";

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const generateObjectId = () => {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
  const random = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
  return timestamp + random;
};

// ─── CONSTANTS ─────────────────────────────────────────────────────────────
const ALL_ROLES = [
  { id: 1, name: "User", value: "user" },
  { id: 2, name: "Head", value: "head" },
];

const MODULES_LIST = [
  "chartering",
  "operations",
  "accounts",
  "agency",
  "shipmanagement",
  "admin",
  "riskmanagement",
  "fuelsupply",
  "knowledgepartner",
];

const EMPTY_USER = {
  firstName: "",
  lastName: "",
  email: "",
  role: "user",
  moduleAccess: [],
  moduleHeads: {},
};

// ─── COMPONENT ─────────────────────────────────────────────────────────────
export default function AddUserForm({
  open,
  handleClose,
  existingHeads = [],
  initialEditData = null,
}) {
  // Initialize with MongoDB ObjectId
  const [users, setUsers] = useState([{ ...EMPTY_USER, _id: generateObjectId() }]);
  const [requestReason, setRequestReason] = useState("");
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialEditData && initialEditData.users) {
        setUsers(initialEditData.users);
        setRequestReason(initialEditData.reason || "");
      } else {
        setUsers([{ ...EMPTY_USER, _id: generateObjectId() }]);
        setRequestReason("");
      }
      setShowError(false);
    }
  }, [open, initialEditData]);

  const [headModalOpen, setHeadModalOpen] = useState(false);
  const [activeUserIndex, setActiveUserIndex] = useState(null);

  /* ── Heads logic ── */
  const availableHeads = useMemo(() => {
    // Include head-role users from form rows so they can be assigned to other rows
    const formHeads = users
      .filter((u) => u.role === "head" && u.firstName && u.email)
      .map((u) => ({
        _id: u._id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        role: "head",
        moduleAccess: u.moduleAccess || [],
      }));

    const combined = [...existingHeads];
    formHeads.forEach((fh) => {
      if (!combined.find((ch) => ch.email === fh.email)) combined.push(fh);
    });
    return combined;
  }, [users, existingHeads]);

  const getDefaultHeadId = () => {
    const superboss = availableHeads.find(
      (h) => h.role === "superboss" || h.role === "admin"
    );
    return superboss ? superboss._id : "";
  };

  const isDuplicateEmail = (email, currentIndex) => {
    if (!email || email.trim() === "") return false;
    return users.some(
      (user, index) =>
        index !== currentIndex &&
        user.email.trim().toLowerCase() === email.trim().toLowerCase()
    );
  };

  /* ── Handlers ── */
  const handleModuleChange = (index, newModules) => {
    const updatedUsers = [...users];
    const currentUser = updatedUsers[index];
    const oldModules = currentUser.moduleAccess || [];
    const currentHeads = currentUser.moduleHeads || {};
    const newHeads = {};
    const defaultHeadId = getDefaultHeadId();
    const defaultHeadObj = availableHeads.find((h) => h._id === defaultHeadId);

    newModules.forEach((mod) => {
      if (currentHeads[mod]) {
        newHeads[mod] = currentHeads[mod];
      } else if (defaultHeadId && defaultHeadObj) {
        newHeads[mod] = [
          {
            _id: defaultHeadObj._id,
            firstName: defaultHeadObj.firstName,
            lastName: defaultHeadObj.lastName,
            email: defaultHeadObj.email,
          },
        ];
      }
    });
    updatedUsers[index] = {
      ...currentUser,
      moduleAccess: newModules,
      moduleHeads: newHeads,
    };

    // If this user is a head, deselect them from other users' moduleHeads
    // for any modules that were removed from this head's access
    if (currentUser.role === "head") {
      const removedModules = oldModules.filter((m) => !newModules.includes(m));
      if (removedModules.length > 0) {
        updatedUsers.forEach((u, i) => {
          if (i === index) return; // skip the head user itself
          removedModules.forEach((mod) => {
            if (u.moduleHeads?.[mod]?.some((h) => h._id === currentUser._id)) {
              updatedUsers[i] = {
                ...updatedUsers[i],
                moduleHeads: {
                  ...updatedUsers[i].moduleHeads,
                  [mod]: [],
                },
              };
            }
          });
        });
      }
    }

    setUsers(updatedUsers);
  };

  const handleChange = (index, key, value) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], [key]: value };

    // Auto-assign superboss when role is changed to "head"
    if (key === "role" && value === "head") {
      const superboss = existingHeads.find((h) => h.role === "superboss");
      if (superboss && updatedUsers[index].moduleAccess?.length > 0) {
        const newHeads = {};
        updatedUsers[index].moduleAccess.forEach((mod) => {
          newHeads[mod] = [
            {
              _id: superboss._id,
              firstName: superboss.firstName,
              lastName: superboss.lastName,
              email: superboss.email,
            },
          ];
        });
        updatedUsers[index].moduleHeads = newHeads;
      }
    }

    setUsers(updatedUsers);
  };

  const handleHeadChange = (userIndex, moduleName, headId) => {
    const updatedUsers = [...users];
    const selectedHead = availableHeads.find((h) => h._id === headId);
    if (selectedHead) {
      updatedUsers[userIndex].moduleHeads = {
        ...updatedUsers[userIndex].moduleHeads,
        [moduleName]: [
          {
            _id: selectedHead._id,
            firstName: selectedHead.firstName,
            lastName: selectedHead.lastName,
            email: selectedHead.email,
          },
        ],
      };
    }
    setUsers(updatedUsers);
  };

  const handleAddRow = (index) => {
    if (!users[index].firstName || !users[index].email) {
      toast.error("Please fill basic details first", { toastId: "fill-details-first" });
      return;
    }
    const currentUser = users[index];
    const newUsers = [...users];
    const newUser = {
      ...EMPTY_USER,
      _id: generateObjectId(),
      role: currentUser.role,
      moduleAccess: [...currentUser.moduleAccess],
      moduleHeads: { ...currentUser.moduleHeads },
    };
    newUsers.splice(index + 1, 0, newUser);
    setUsers(newUsers);
  };

  const handleRemoveRow = (index) => {
    if (users.length > 1) setUsers(users.filter((_, i) => i !== index));
  };

  const openHeadAssignment = (index) => {
    setActiveUserIndex(index);
    setHeadModalOpen(true);
  };
  const closeHeadAssignment = () => {
    setHeadModalOpen(false);
    setActiveUserIndex(null);
  };

  const handleSubmit = () => {
    let isValid = true;
    users.forEach((u) => {
      if (!u.firstName || !u.lastName || !u.email || u.moduleAccess.length === 0 || !isValidEmail(u.email))
        isValid = false;
      u.moduleAccess.forEach((mod) => {
        if (!u.moduleHeads[mod] || u.moduleHeads[mod].length === 0)
          isValid = false;
      });
    });
    if (!isValid) {
      setShowError(true);
      toast.error("Please ensure all fields are correctly filled and heads assigned.", { toastId: "validation-error" });
      return;
    }

    setLoading(true);
    const requestData = {
      type: "requestUsers",
      users,
      reason: requestReason,
      createdAt: new Date().toISOString(),
    };

    updateUser({
      request: requestData,
    })
      .then(() => {
        try {
          const authDataString = localStorage.getItem("auth");
          if (authDataString) {
            const authData = JSON.parse(authDataString);
            authData.request = requestData;
            localStorage.setItem("auth", JSON.stringify(authData));
          }
        } catch (error) {
          console.error("Local storage update failed", error);
        }
        setLoading(false);
        toast.success("Requests submitted successfully", { toastId: "submit-success" });
        handleClose();
      })
      .catch(() => {
        setLoading(false);
        toast.error("Submission failed", { toastId: "submit-failed" });
      });
  };

  const getHeadStatus = (user) => {
    if (!user.moduleAccess || user.moduleAccess.length === 0)
      return { status: "empty", label: "Select modules first" };

    // Head-role users have auto-assigned superboss
    if (user.role === "head")
      return {
        status: "auto",
        label: "Auto (Boss)",
        icon: <SupervisorAccountIcon style={{ fontSize: 15 }} />,
      };

    let missingCount = 0;
    user.moduleAccess.forEach((mod) => {
      if (!user.moduleHeads[mod] || user.moduleHeads[mod].length === 0)
        missingCount++;
    });

    if (missingCount > 0)
      return {
        status: "pending",
        label: `${missingCount} head${missingCount > 1 ? "s" : ""} needed`,
        icon: <ErrorOutlineIcon style={{ fontSize: 15 }} />,
      };

    return {
      status: "complete",
      label: `${user.moduleAccess.length} assigned`,
      icon: <CheckCircleIcon style={{ fontSize: 15 }} />,
    };
  };

  /* ── Render ── */
  return (
    <>
      {/* ════ MAIN DIALOG ════ */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 200 }}
        classes={{
          paper: "rounded-none overflow-hidden border border-[#cbd5e1] shadow-2xl bg-white",
        }}
      >
        {/* Header Bar */}
        <div className="flex items-center justify-between w-full px-4 py-2 bg-[#f8fafc] border-b border-[#cbd5e1]">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-[#1e293b]">
              {initialEditData ? "Edit User Request" : "New User Requests"}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-slate-500 hover:text-slate-900 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
            title="Close"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* Scrollable Body */}
        <DialogContent className="p-3 bg-white scroll-thin" style={{ overflowY: "auto", maxHeight: "80vh" }}>
          {/* Excel-style Data Grid Table */}
          <div className="w-full overflow-x-auto scroll-thin">
            <table className="w-full table-fixed border-collapse border border-[#cbd5e1] text-[11px] text-[#000000]">
              <thead>
                <tr className="bg-[#e2e8f0] text-left text-[11px] font-medium text-[#000000]">
                  <th className="border border-[#cbd5e1] px-1 py-1 w-[36px] text-center font-medium">#</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[15%] font-medium">First Name</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[15%] font-medium">Last Name</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[23%] font-medium">Email Address</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[75px] font-medium">Role</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[25%] font-medium">Module Access</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 w-[130px] font-medium">Head Assignment</th>
                  <th className="border border-[#cbd5e1] px-1 py-1 w-[40px] text-center font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  const hasError =
                    showError &&
                    (!user.firstName || !user.email || user.moduleAccess.length === 0);
                  const headStatus = getHeadStatus(user);

                  return (
                    <tr key={user._id || index} className="hover:bg-[#f8fafc] transition-colors">
                      {/* Index */}
                      <td className="border border-[#cbd5e1] px-1 py-0.5 text-center bg-[#f8fafc] text-gray-600 font-mono text-[11px]">
                        {index + 1}
                      </td>

                      {/* First Name */}
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6] overflow-hidden max-w-0">
                        <input
                          type="text"
                          placeholder="First name"
                          value={user.firstName}
                          onChange={(e) => handleChange(index, "firstName", e.target.value)}
                          className={`w-full h-[24px] px-2 text-[11px] bg-[#ffffd6] text-[#000000] border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-light ${hasError && !user.firstName ? "bg-[#fef2f2] text-red-600" : ""
                            }`}
                        />
                      </td>

                      {/* Last Name */}
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6] overflow-hidden max-w-0">
                        <input
                          type="text"
                          placeholder="Last name"
                          value={user.lastName}
                          onChange={(e) => handleChange(index, "lastName", e.target.value)}
                          className={`w-full h-[24px] px-2 text-[11px] bg-[#ffffd6] text-[#000000] border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-light ${hasError && !user.lastName ? "bg-[#fef2f2] text-red-600" : ""
                            }`}
                        />
                      </td>

                      {/* Email */}
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6] overflow-hidden max-w-0">
                        <input
                          type="email"
                          placeholder="Email address"
                          value={user.email}
                          onChange={(e) => handleChange(index, "email", e.target.value)}
                          className={`w-full h-[24px] px-2 text-[11px] bg-[#ffffd6] text-[#000000] border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-light ${(hasError && (!user.email || !isValidEmail(user.email))) || isDuplicateEmail(user.email, index)
                            ? "bg-[#fef2f2] text-red-600"
                            : ""
                            }`}
                        />
                      </td>

                      {/* Role */}
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6] overflow-hidden">
                        <select
                          value={user.role}
                          onChange={(e) => handleChange(index, "role", e.target.value)}
                          className="w-full h-[24px] px-1 text-[11px] bg-[#ffffd6] text-[#000000] border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] cursor-pointer font-light capitalize"
                        >
                          {ALL_ROLES.map((r) => (
                            <option key={r.id} value={r.value}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Modules */}
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6] overflow-hidden max-w-0">
                        <Select
                          multiple
                          value={user.moduleAccess}
                          onChange={(e) => handleModuleChange(index, e.target.value)}
                          displayEmpty
                          className="w-full h-[24px] text-[11px] bg-[#ffffd6] border-none overflow-hidden"
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return <span className="text-gray-400 text-[11px] px-1 block truncate">Select modules…</span>;
                            }
                            const labelText = selected.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(", ");
                            return (
                              <span className="text-[#000000] text-[11px] px-1 truncate block font-light" title={labelText}>
                                {labelText}
                              </span>
                            );
                          }}
                          disableUnderline
                        >
                          {MODULES_LIST.map((name) => (
                            <MenuItem key={name} value={name} className="!text-[11px] !py-1">
                              <Checkbox
                                checked={user.moduleAccess.indexOf(name) > -1}
                                size="small"
                                className="!p-0 !mr-1"
                              />
                              <ListItemText
                                primary={name.charAt(0).toUpperCase() + name.slice(1)}
                                primaryTypographyProps={{ style: { fontSize: "11px" } }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </td>

                      {/* Head Assignment */}
                      <td className="border border-[#cbd5e1] p-0 bg-white overflow-hidden max-w-0">
                        <button
                          onClick={() => openHeadAssignment(index)}
                          disabled={user.moduleAccess.length === 0 || user.role === "head"}
                          className={`w-full h-[24px] px-1.5 text-[11px] font-light border-none text-left flex items-center justify-between cursor-pointer ${headStatus.status === "pending"
                            ? "bg-[#fef3c7] text-[#92400e]"
                            : headStatus.status === "complete" || headStatus.status === "auto"
                              ? "bg-[#dcfce7] text-[#166534]"
                              : "bg-gray-50 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                          <span className="truncate">{headStatus.label}</span>
                          <ChevronRightIcon style={{ fontSize: 14 }} />
                        </button>
                      </td>

                      {/* Delete */}
                      <td className="border border-[#cbd5e1] p-0 text-center bg-[#f8fafc]">
                        <button
                          onClick={() => handleRemoveRow(index)}
                          disabled={users.length === 1}
                          className="p-0.5 text-gray-400 hover:text-red-600 disabled:opacity-30 border-none bg-transparent cursor-pointer"
                          title="Remove row"
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add Row Button */}
          <div className="mt-2 flex items-center justify-between">
            <button
              onClick={() => handleAddRow(users.length - 1)}
              className="h-[24px] px-2.5 text-[11px] font-medium text-[#313C4C] bg-slate-100 hover:bg-slate-200 border border-[#cbd5e1] flex items-center gap-1 cursor-pointer transition-colors"
            >
              <AddCircleIcon style={{ fontSize: 14 }} />
              <span>Add User Row</span>
            </button>
          </div>

          {/* Request Notes */}
          <div className="mt-3">
            <label className="block text-[11px] font-medium text-gray-700 mb-1 uppercase tracking-wider">
              Request Notes <span className="text-gray-400 font-medium lowercase">(optional)</span>
            </label>
            <textarea
              rows={2}
              placeholder="Add any context or justification for this access request…"
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              className="w-full p-2 text-[11px] text-[#000000] border border-[#cbd5e1] outline-none focus:border-[#3b82f6] focus:ring-1 focus:ring-[#3b82f6] font-light resize-y bg-white"
            />
          </div>
        </DialogContent>

        {/* Footer */}
        <div className="flex items-center justify-between w-full px-4 py-2 bg-slate-50 border-t border-[#cbd5e1]">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${users.filter(
                (u) => u.firstName && u.email && u.moduleAccess.length > 0
              ).length === users.length
                ? "bg-emerald-500"
                : "bg-amber-500"
                }`}
            />
            <span className="text-[11px] text-gray-600">
              {
                users.filter(
                  (u) => u.firstName && u.email && u.moduleAccess.length > 0
                ).length
              }{" "}
              of {users.length} users ready
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="h-[28px] px-3.5 text-[11px] font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-none cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="h-[28px] px-3.5 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded-none flex items-center gap-1.5 uppercase cursor-pointer"
            >
              {loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon style={{ fontSize: 13 }} className="text-white" />
              )}
              <span>{loading ? "Submitting..." : initialEditData ? "Update Request" : "Submit Request"}</span>
            </button>
          </div>
        </div>
      </Dialog>

      {/* ════ HEAD ASSIGNMENT MODAL ════ */}
      {activeUserIndex !== null && (
        <Dialog
          open={headModalOpen}
          onClose={closeHeadAssignment}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 150 }}
          classes={{
            paper: "rounded-none overflow-hidden border border-[#cbd5e1] shadow-2xl bg-white",
          }}
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between w-full px-4 py-2 bg-[#f8fafc] border-b border-[#cbd5e1]">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-[#1e293b]">
                Assign Module Heads
              </span>
              <span className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-700 border border-[#cbd5e1] font-mono">
                Row #{activeUserIndex + 1} ({users[activeUserIndex]?.firstName || "User"})
              </span>
            </div>
            <button
              onClick={closeHeadAssignment}
              className="p-1 text-slate-500 hover:text-slate-900 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
              title="Close"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          </div>

          <DialogContent className="p-3 bg-white scroll-thin" style={{ overflowY: "auto", maxHeight: "60vh" }}>
            <table className="w-full border-collapse border border-[#cbd5e1] text-[11px] text-[#000000]">
              <thead>
                <tr className="bg-[#e2e8f0] text-left text-[11px] font-medium text-[#000000]">
                  <th className="border border-[#cbd5e1] px-2 py-1 font-medium w-1/3">Module Name</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 font-medium">Assigned Head</th>
                  <th className="border border-[#cbd5e1] px-2 py-1 font-medium w-24 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {users[activeUserIndex]?.moduleAccess.map((mod) => {
                  const headArr = users[activeUserIndex].moduleHeads[mod] || [];
                  const currentHeadId = headArr.length > 0 ? headArr[0]._id : "";
                  const modLabel = mod.charAt(0).toUpperCase() + mod.slice(1);
                  const currentUser = users[activeUserIndex];

                  const availableOptions = availableHeads
                    ?.filter((h) => h._id !== currentUser._id)
                    .filter((h) => h.role === "superboss" || h.moduleAccess?.includes(mod));

                  return (
                    <tr key={mod} className="hover:bg-[#f8fafc] transition-colors">
                      <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] font-medium text-slate-700 capitalize">
                        {modLabel}
                      </td>
                      <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                        <select
                          value={currentHeadId || ""}
                          onChange={(e) => handleHeadChange(activeUserIndex, mod, e.target.value)}
                          className="w-full h-[26px] px-2 text-[11px] bg-[#ffffd6] text-[#000000] border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] cursor-pointer font-light"
                        >
                          <option value="">-- Select Head --</option>
                          {availableOptions?.map((h) => (
                            <option key={h._id} value={h._id}>
                              {h.firstName} {h.lastName} ({h.email})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="border border-[#cbd5e1] px-2 py-1 text-center bg-white">
                        {currentHeadId ? (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#dcfce7] text-[#166534] font-medium border border-[#bbf7d0]">
                            Assigned
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#fef3c7] text-[#92400e] font-medium border border-[#fde68a]">
                            Required
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DialogContent>

          {/* Footer Bar */}
          <div className="flex items-center justify-end px-4 py-2 bg-slate-50 border-t border-[#cbd5e1]">
            <button
              onClick={closeHeadAssignment}
              className="h-[28px] px-3.5 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded-none uppercase cursor-pointer"
            >
              Done
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}